import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Navigation } from '@quizzach/components';
import { useWindowListener } from '@quizzach/contexts';
import { Reveal } from '@quizzach/motion';
import {
	BtnLine,
	CookieArticle,
	CookieList,
	CookieTable,
	LegalHead,
	LegalPage,
} from '@quizzach/styles';
import { scrollToTop } from '@quizzach/utils';

type CookieRow = {
	name: string;
	duration: string;
	purpose: string;
	type: string;
};

export const CookiePolicy = () => {
	const { t } = useTranslation();
	const { touchDevice } = useWindowListener();

	const handleResetCookieSettings = () => {
		['cookies_consent', 'analytical', 'marketing'].forEach((name) => {
			document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
		});
		location.reload();
	};

	useEffect(() => {
		scrollToTop();
	}, []);

	const cookieRows: CookieRow[] = [
		{
			name: 'cookies_consent',
			duration: t('footer.cookies.durationYear'),
			purpose: t('footer.cookies.purposeConsent'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'analytic_consent',
			duration: t('footer.cookies.durationYear'),
			purpose: t('footer.cookies.purposeAnalyticConsent'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'marketing_consent',
			duration: t('footer.cookies.durationYear'),
			purpose: t('footer.cookies.purposeMarketingConsent'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'userToken',
			duration: t('footer.cookies.durationLogout'),
			purpose: t('footer.cookies.purposeUserToken'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'quiz-*',
			duration: t('footer.cookies.durationManual'),
			purpose: t('footer.cookies.purposeQuizPrefs'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'algolia-client-*',
			duration: t('footer.cookies.durationManual'),
			purpose: t('footer.cookies.purposeAlgolia'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'generatorQuiz, quickQuiz',
			duration: t('footer.cookies.durationCart'),
			purpose: t('footer.cookies.purposeCartState'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'PHPSESSID',
			duration: t('footer.cookies.durationSession'),
			purpose: t('footer.cookies.purposePhpSession'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: 'ACCOUNT_CHOOSER',
			duration: t('footer.cookies.durationSession'),
			purpose: t('footer.cookies.purposeAccountChooser'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: '__Host-*, __Secure-*, NID, SAPISID, SSID, APISID i ostali SID*',
			duration: t('footer.cookies.durationYear'),
			purpose: t('footer.cookies.purposeGoogleSession'),
			type: t('footer.cookies.typeNecessary'),
		},
		{
			name: '_ga, _ga_*',
			duration: t('footer.cookies.durationYear'),
			purpose: t('footer.cookies.purposeGa'),
			type: t('footer.cookies.typeAnalytical'),
		},
		{
			name: '_fbp, _fbc',
			duration: t('footer.cookies.durationYear'),
			purpose: t('footer.cookies.purposeFbPixel'),
			type: t('footer.cookies.typeAnalytical'),
		},
	];

	return (
		<LegalPage>
			<Navigation />

			<LegalHead>
				<Reveal as='h1'>{t('footer.cookies.title')}</Reveal>
				<Reveal delay={1}>
					<span className='hand'>{t('footer.cookies.lastUpdated')}</span>
				</Reveal>
			</LegalHead>

			<CookieArticle data-id='cookie-policy-container'>
				<div className='col'>
					<h2>{t('footer.cookies.whatTitle')}</h2>
					<p>{t('footer.cookies.whatP1')}</p>
					<p>{t('footer.cookies.whatP2')}</p>

					<h2>{t('footer.cookies.managingTitle')}</h2>
					<p>{t('footer.cookies.managingP1')}</p>

					<h2>{t('footer.cookies.typesTitle')}</h2>

					<h3>{t('footer.cookies.requiredTitle')}</h3>
					<p>{t('footer.cookies.requiredText')}</p>

					<h3>{t('footer.cookies.marketingTitle')}</h3>
					<p>{t('footer.cookies.marketingText')}</p>

					<h3>{t('footer.cookies.analyticsTitle')}</h3>
					<p>{t('footer.cookies.analyticsText')}</p>
				</div>

				{touchDevice ? (
					<CookieList>
						{cookieRows.map((row) => (
							<div className='item' key={row.name}>
								<b>
									{t('footer.cookies.colName')}: {row.name}
								</b>
								<span>
									{t('footer.cookies.colDuration')}: {row.duration}
								</span>
								<span>
									{t('footer.cookies.colPurpose')}: {row.purpose}
								</span>
								<span>
									{t('footer.cookies.colType')}: {row.type}
								</span>
							</div>
						))}
					</CookieList>
				) : (
					<CookieTable>
						<div className='row head'>
							<span>{t('footer.cookies.colName')}</span>
							<span>{t('footer.cookies.colDuration')}</span>
							<span>{t('footer.cookies.colPurpose')}</span>
							<span>{t('footer.cookies.colType')}</span>
						</div>
						{cookieRows.map((row) => (
							<div className='row' key={row.name}>
								<b>{row.name}</b>
								<span>{row.duration}</span>
								<span>{row.purpose}</span>
								<span>{row.type}</span>
							</div>
						))}
					</CookieTable>
				)}

				<div className='col'>
					<BtnLine type='button' onClick={handleResetCookieSettings}>
						{t('footer.cookies.changeSettings')}
					</BtnLine>
					<p style={{ marginTop: '28px' }}>{t('footer.cookies.outro')}</p>
					<p>{t('footer.cookies.contactNote')}</p>
				</div>
			</CookieArticle>
		</LegalPage>
	);
};
